import { AssessmentSurveyAnswer } from './assessmentSurvey'
import { PostMessage } from './events'
import { Address } from './address'
import { Choice } from './choice'
import { Config } from './config'
import { AVAILABLE_SERVICE, Consultation, CONSULTATION_TYPE } from './consultation'
import { Country } from './country'
import { DoseSpotPharmaciesResponse, IndicationSurvey, Pharmacy } from './indication'
import { Module } from './module'
import { ReminderUi } from './reminder'
import { User } from './user'
import { CountryCode } from './components'
import { HealthNavigatorResponse, UserMessageFeedback } from './navigator'

export enum ACTION_TYPE {
  /*
    Initial config for the current partner.

    () => {
      config: Config
    }
  */
  GET_CONFIG = 'GET_CONFIG',

  /*
    First time user enters the app.

    (uniqueId?: string, userId?: string, language?: string, country?: Country, consultationType?: CONSULTATION_TYPE) => {
      user: {
        data: {
          uniqueId: string, // randomized if not existing user
          ... rest of user data if existing user
        },
        routeConditions: {
          group: "test|control", // randomized if not existing user
        }
      },
      module: Module, // initial module
    }
  */
  GET_INITIAL_MODULE = 'GET_INITIAL_MODULE',

  /*
    When user clicks back on a first step of a module.

    (uniqueId: string, currentModuleId: string, currentStepId: string) => {
      module: Module // previous module from db
    }
  */
  GET_PREVIOUS_MODULE = 'GET_PREVIOUS_MODULE',

  /*
    (uniqueId: string, moduleId: string) => {
      module: Module // next module
    }
  */
  GET_NEXT_MODULE = 'GET_NEXT_MODULE',

  /*
    (user: User) => {
      user: User // updated user
    }
  */
  UPDATE_USER = 'UPDATE_USER',

  /*
    (user: User) => {
      user: User // created user
    }
  */
  CREATE_USER = 'CREATE_USER',

  /*
    (choice: Choice) => {
      choice: Choice // choice set
    }
  */
  SET_CHOICE = 'SET_CHOICE',

  /*
    (choices: Choice[]) => {
      choices: Choice // reminder
    }
  */
  SET_REMINDER_WEEKLY = 'SET_REMINDER_WEEKLY',
  SET_REMINDER_PHYSICIAN = 'SET_REMINDER_PHYSICIAN',
  SET_REMINDER_PHARMACY = 'SET_REMINDER_PHARMACY',

  /*
    (userId: string) => {
      user: User // user
    }
  */
  GET_USER = 'GET_USER',

  /*
    (uniqueId: string) => {
      user: User // user
    }
  */
  SET_FLOW_COMPLETED = 'SET_FLOW_COMPLETED',

  /*
    () => {
      countries: Country[]
    }
  */
  GET_PHYSICIAN_COUNTRIES = 'GET_PHYSICIAN_COUNTRIES',

  /*
    (uniqueId: string, type: ROUTE_CONDITION_TYPE) => {
      user: User // User with updated
    }
  */
  GET_ROUTE_CONDITIONS = 'GET_ROUTE_CONDITIONS',

  /*
    () => {
      user: User // User with updated data
    }
  */
  SET_USER_INFO = 'SET_USER_INFO',

  /*
  (uniqueId: string) => Consultation
  */
  GET_CURRENT_USER_CONSULTATION = 'GET_CURRENT_USER_CONSULTATION',

  /*
    (uniqueId: string, question: string, attachments: string[]) => Consultation
  */
  CREATE_MEDICAL_CONSULTATION = 'CREATE_MEDICAL_CONSULTATION',

  /*
    (uniqueId: string, question: string, attachments: string[]) => Consultation
  */
  CREATE_MEDICAL_FOLLOW_UP_CONSULTATION = 'CREATE_MEDICAL_FOLLOW_UP_CONSULTATION',

  /*
    (uniqueId: string, question: string) => Consultation
  */
  CREATE_PRESCRIPTION_REQUEST = 'CREATE_PRESCRIPTION_REQUEST',

  /*
    (uniqueId: string) => Consultation | null
  */
  CANCEL_CONSULTATION = 'CANCEL_CONSULTATION',

  /*
    (uniqueId: string, rate: number) => Consultation
  */
  RATE_CONSULTATION = 'RATE_CONSULTATION_V2',

  /*
    (uniqueId: string, file: string) => {
      url: string
    }
  */
  UPLOAD_ATTACHMENT = 'UPLOAD_ATTACHMENT',

  /*
    (uniqueId: string) => {
      url: string
    }
  */
  GET_CONSULTATION_PDF = 'GET_CONSULTATION_PDF',

  /*
    (uniqueId: string) => void
  */
  CLOSE_SESSION = 'CLOSE_SESSION',

  /*
    (uniqueId: string) => {
      user: User // user
    }
  */
  RESET_TRACES = 'RESET_TRACES',

  /*
    (consultationType: CONSULTATION_TYPE.PRESCRIPTION | CONSULTATION_TYPE.MENTAL, indicationSurveyId: number, uniqueId: string) => Module
  */
  GET_INDICATION_SURVEY_MODULE = 'GET_INDICATION_SURVEY_MODULE',

  /*
    (uniqueId: string, indicationSurvey: IndicationSurvey) => Indication[]
  */
  SET_INDICATION_ANSWERS = 'SET_INDICATION_ANSWERS',

  /*
    (uniqueId: string, pharmacyId: string) => { pharmacyId: string }
  */
  SET_PHARMACY_CHOICE = 'SET_PHARMACY_CHOICE',

  /*
    (uniqueId: string, phone: string, consultationId: string) => { user: User, consultation: Consultation }
  */
  SET_CASE_NOTIFICATION = 'SET_CASE_NOTIFICATION',

  /*
    (uniqueId: string, userId: string) => { session: { id: string }}
  */
  HANDLE_SESSION = 'HANDLE_SESSION',

  /*
    (uniqueId: string, question: string, attachments: string[]) => Consultation
  */
  CREATE_VIDEO_CONSULTATION = 'CREATE_VIDEO_CONSULTATION',

  /*
    (uniqueId: string) => Consultation
  */
  VIDEO_CALL_HUNG_UP = 'VIDEO_CALL_HUNG_UP',

  /*
    (uniqueId: string) => Consultation
  */
  VIDEO_CALL_JOINED = 'VIDEO_CALL_JOINED',

  /*
    (uniqueId: string, filter: { state: string, query: string}) => Pharmacy[]
  */
  GET_PHARMACIES = 'GET_PHARMACIES_V2',

  /*
    (uniqueId: string, pharmacyId: string) => void
  */
  SET_FULFILLMENT_COLLECT = 'SET_FULFILLMENT_COLLECT',

  /*
    (uniqueId: string) => CountryCode[]
  */
  GET_DELIVERY_COUNTRIES = 'GET_DELIVERY_COUNTRIES',

  /*
    (uniqueId: string, address: Address) => void
  */
  SET_FULFILLMENT_DELIVERY = 'SET_FULFILLMENT_DELIVERY',

  /*
    (uniqueId: string) => Consultation
  */
  SET_PAYMENT_STARTED = 'SET_PAYMENT_STARTED',

  /*
    (uniqueId: string, address: string, city?: string, country?: string, zipCode?: string, state?: string) => { consultation: Consultation }
  */
  SET_FULFILLMENT_USER_ADDRESS = 'SET_FULFILLMENT_USER_ADDRESS',

  /*
    (uniqueId: string, consultationType: AVAILABLE_SERVICE) => boolean
  */
  CHECK_CONSULTATION_LIMIT_EXCEEDED = 'CHECK_CONSULTATION_LIMIT_EXCEEDED',

  /*
    (uniqueId: string, to: CONSULTATION_TYPE) => Consultation
  */
  SWAP_CONSULTATION = 'SWAP_CONSULTATION',
  /*
    (userId: string, language:string) => User
  */
  UPDATE_USER_LANGUAGE = 'UPDATE_USER_LANGUAGE',

  /*
    (uniqueId: string, address: string, phone: string) => {  consultation: Consultation }
  */
  SET_FULFILLMENT_PATIENT_INFO = 'SET_FULFILLMENT_PATIENT_INFO',

  /*
    (uniqueId: string, text: string) => void
  */
  SEND_HEALTH_NAVIGATOR_MESSAGE = 'SEND_HEALTH_NAVIGATOR_MESSAGE',

  /*
    (uniqueId: string) => HealthNavigatorResponse
  */
  START_HEALTH_NAVIGATOR = 'START_HEALTH_NAVIGATOR',

  /*
    (uniqueId: string) => HealthNavigatorResponse
  */
  FINISH_HEALTH_NAVIGATOR = 'FINISH_HEALTH_NAVIGATOR',

  /*
    (uniqueId: string, serviceType: SERVICE_TYPE) => HealthNavigatorResponse
  */
  HEALTH_NAVIGATOR_SERVICE_SELECTED = 'HEALTH_NAVIGATOR_SERVICE_SELECTED',
  /*
    (uniqueId: string) => Consultation
  */
  VIEW_REFERRAL_URGENCY = 'VIEW_REFERRAL_URGENCY',
  /*
      (uniqueId: string) => Consultation
    */
  REGISTER_REFERRAL = 'REGISTER_REFERRAL',

  /*
    (consultationType: CONSULTATION_TYPE.MENTAL, assessmentSurveyId: number, uniqueId: string) => Module
  */
  GET_ASSESSMENT_SURVEY_MODULE = 'GET_ASSESSMENT_SURVEY_MODULE',

  /*
    (uniqueId: string, assessmentSurveyAnswer: AssessmentSurveyAnswer) => void
  */
  SET_ASSESSMENT_SURVEY_ANSWERS = 'SET_ASSESSMENT_SURVEY_ANSWERS',

  /*
    (uniqueId: string, question: string) => Consultation
  */
  CREATE_MENTAL_HEALTH_CONSULTATION = 'CREATE_MENTAL_HEALTH_CONSULTATION',

  /*
    (userId: string, event: PostMessage) => void
  */
  TRACK_EVENT = 'TRACK_EVENT',

  /*
    (uniqueId: string, conversationId: string) => { summary: string }
  */
  GET_CONVERSATION_SUMMARY = 'GET_CONVERSATION_SUMMARY',

  /*
    (uniqueId: string, connectionId?: string) => { isConnected: boolean }
  */
  IS_WEBSOCKET_CONNECTED = 'IS_WEBSOCKET_CONNECTED',

  /*
    (uniqueId: string, messageId: string, ...) => UserMessageFeedback
  */
  STORE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK = 'STORE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK',

  /*
    (uniqueId: string, feedbackId: string, messageId: string) => void
  */
  DELETE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK = 'DELETE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK',

  /*
    (uniqueId: string, conversationId: string, ...) => HealthNavigatorResponse
  */
  STORE_HEALTH_NAVIGATOR_CONVERSATION_FEEDBACK = 'STORE_HEALTH_NAVIGATOR_CONVERSATION_FEEDBACK',

  /*
    (uniqueId: string) => Consultation
  */
  CLOSE_CONSULTATION = 'CLOSE_CONSULTATION'
}

export enum ROUTE_CONDITION_TYPE {
  SANOFI_BMI = 'SANOFI_BMI'
}

export type OnLoadAction = ACTION_TYPE.SET_FLOW_COMPLETED

export type Action<T extends ACTION_TYPE> = T extends ACTION_TYPE.GET_CONFIG
  ? {
      type: T
      payload: {}
      response: {
        config: Config
      }
    }
  : T extends ACTION_TYPE.GET_INITIAL_MODULE
    ? {
        type: T
        payload: {
          uniqueId?: string
          userId?: string // partnerUserId
          language?: string
          country?: Country
          serviceType?: AVAILABLE_SERVICE
        }
        response: {
          user: User
          module: Module
        }
      }
    : T extends ACTION_TYPE.GET_PREVIOUS_MODULE
      ? {
          type: T
          payload: {
            uniqueId: string
          }
          response: {
            module: Module
          }
        }
      : T extends ACTION_TYPE.GET_NEXT_MODULE
        ? {
            type: T
            payload: {
              uniqueId: string
              moduleId: string
            }
            response: {
              module: Module
            }
          }
        : T extends ACTION_TYPE.UPDATE_USER | ACTION_TYPE.CREATE_USER
          ? {
              type: T
              payload: {
                user: User['data']
              }
              response: User
            }
          : T extends ACTION_TYPE.SET_CHOICE
            ? {
                type: T
                payload: {
                  uniqueId: string
                  choice: Choice
                }
                response: {
                  choice: Choice
                }
              }
            : T extends ACTION_TYPE.SET_REMINDER_PHARMACY | ACTION_TYPE.SET_REMINDER_PHYSICIAN | ACTION_TYPE.SET_REMINDER_WEEKLY
              ? {
                  type: T
                  payload: {
                    uniqueId: string
                    reminder?: string
                    timezoneOffset: number
                  }
                  response: {
                    reminder: ReminderUi
                  }
                }
              : T extends ACTION_TYPE.GET_USER
                ? {
                    type: T
                    payload: {
                      userId: string
                    }
                    response: User
                  }
                : T extends ACTION_TYPE.SET_FLOW_COMPLETED
                  ? {
                      type: T
                      payload: {
                        uniqueId: string
                      }
                      response: User
                    }
                  : T extends ACTION_TYPE.GET_PHYSICIAN_COUNTRIES
                    ? {
                        type: T
                        payload: Record<string, never>
                        response: Country[]
                      }
                    : T extends ACTION_TYPE.GET_ROUTE_CONDITIONS
                      ? {
                          type: T
                          payload: {
                            uniqueId: string
                            type: ROUTE_CONDITION_TYPE
                          }
                          response: User
                        }
                      : T extends ACTION_TYPE.SET_USER_INFO
                        ? {
                            type: T
                            payload: Partial<User['data']>
                            response: User
                          }
                        : T extends ACTION_TYPE.GET_CURRENT_USER_CONSULTATION
                          ? {
                              type: T
                              payload: {
                                uniqueId: string
                              }
                              response: Consultation | null // Last consultation or null if user has no consultations yet
                            }
                          : T extends ACTION_TYPE.CREATE_MEDICAL_CONSULTATION
                            ? {
                                type: T
                                payload: {
                                  uniqueId: string
                                  question: string
                                  attachments: string[]
                                }
                                response: Consultation
                              }
                            : T extends ACTION_TYPE.CREATE_MEDICAL_FOLLOW_UP_CONSULTATION
                              ? {
                                  type: T
                                  payload: {
                                    uniqueId: string
                                    question: string
                                    attachments: string[]
                                  }
                                  response: Consultation
                                }
                              : T extends ACTION_TYPE.CANCEL_CONSULTATION
                                ? {
                                    type: T
                                    payload: {
                                      uniqueId: string
                                    }
                                    response: Consultation | null
                                  }
                                : T extends ACTION_TYPE.RATE_CONSULTATION
                                  ? {
                                      type: T
                                      payload: {
                                        uniqueId: string
                                        rating: number | null
                                      }
                                      response: Consultation
                                    }
                                  : T extends ACTION_TYPE.UPLOAD_ATTACHMENT
                                    ? {
                                        type: T
                                        payload: {
                                          uniqueId: string
                                          file: string // base64
                                        }
                                        response: {
                                          url: string
                                        }
                                      }
                                    : T extends ACTION_TYPE.GET_CONSULTATION_PDF
                                      ? {
                                          type: T
                                          payload: {
                                            uniqueId: string
                                          }
                                          response: {
                                            url: string
                                          }
                                        }
                                      : T extends ACTION_TYPE.RESET_TRACES
                                        ? {
                                            type: T
                                            payload: {
                                              uniqueId: string
                                            }
                                            response: User
                                          }
                                        : T extends ACTION_TYPE.GET_INDICATION_SURVEY_MODULE
                                          ? {
                                              type: T
                                              payload: {
                                                consultationType: CONSULTATION_TYPE.PRESCRIPTION | CONSULTATION_TYPE.MENTAL
                                                indicationSurveyId: number
                                                uniqueId: string
                                              }
                                              response: Module
                                            }
                                          : T extends ACTION_TYPE.SET_INDICATION_ANSWERS
                                            ? {
                                                type: T
                                                payload: {
                                                  uniqueId: string
                                                  indicationSurvey: IndicationSurvey
                                                }
                                                response: IndicationSurvey
                                              }
                                            : T extends ACTION_TYPE.CREATE_PRESCRIPTION_REQUEST
                                              ? {
                                                  type: T
                                                  payload: {
                                                    uniqueId: string
                                                    question: string
                                                  }
                                                  response: Consultation
                                                }
                                              : T extends ACTION_TYPE.SET_PHARMACY_CHOICE
                                                ? {
                                                    type: T
                                                    payload: {
                                                      uniqueId: string
                                                      pharmacyId: string
                                                    }
                                                    response: {
                                                      pharmacyId: string
                                                    }
                                                  }
                                                : T extends ACTION_TYPE.SET_CASE_NOTIFICATION
                                                  ? {
                                                      type: T
                                                      payload: {
                                                        uniqueId: string
                                                        phone: string
                                                        consultationId: string
                                                      }
                                                      response: {
                                                        user: User
                                                        consultation: Consultation
                                                      }
                                                    }
                                                  : T extends ACTION_TYPE.HANDLE_SESSION
                                                    ? {
                                                        type: T
                                                        payload: {
                                                          uniqueId: string
                                                        }
                                                        response: {
                                                          session: {
                                                            id: string
                                                          }
                                                        }
                                                      }
                                                    : T extends ACTION_TYPE.CREATE_VIDEO_CONSULTATION
                                                      ? {
                                                          type: T
                                                          payload: {
                                                            uniqueId: string
                                                            question: string
                                                            attachments: string[]
                                                          }
                                                          response: Consultation
                                                        }
                                                      : T extends ACTION_TYPE.VIDEO_CALL_HUNG_UP
                                                        ? {
                                                            type: T
                                                            payload: {
                                                              uniqueId: string
                                                            }
                                                            response: Consultation
                                                          }
                                                        : T extends ACTION_TYPE.VIDEO_CALL_JOINED
                                                          ? {
                                                              type: T
                                                              payload: {
                                                                uniqueId: string
                                                              }
                                                              response: Consultation
                                                            }
                                                          : T extends ACTION_TYPE.GET_PHARMACIES
                                                            ? {
                                                                type: T
                                                                payload: {
                                                                  uniqueId: string
                                                                  filter: unknown
                                                                }
                                                                response: DoseSpotPharmaciesResponse | Pharmacy[]
                                                              }
                                                            : T extends ACTION_TYPE.SET_FULFILLMENT_COLLECT
                                                              ? {
                                                                  type: T
                                                                  payload: {
                                                                    uniqueId: string
                                                                    pharmacyId: string
                                                                  }
                                                                  response: Consultation
                                                                }
                                                              : T extends ACTION_TYPE.GET_DELIVERY_COUNTRIES
                                                                ? {
                                                                    type: T
                                                                    payload: {
                                                                      uniqueId: string
                                                                    }
                                                                    response: {
                                                                      countries: CountryCode[]
                                                                    }
                                                                  }
                                                                : T extends ACTION_TYPE.SET_FULFILLMENT_DELIVERY
                                                                  ? {
                                                                      type: T
                                                                      payload: {
                                                                        uniqueId: string
                                                                        address: Address
                                                                      }
                                                                      response: Consultation
                                                                    }
                                                                  : T extends ACTION_TYPE.SET_PAYMENT_STARTED
                                                                    ? {
                                                                        type: T
                                                                        payload: {
                                                                          uniqueId: string
                                                                        }
                                                                        response: Consultation
                                                                      }
                                                                    : T extends ACTION_TYPE.SET_FULFILLMENT_USER_ADDRESS
                                                                      ? {
                                                                          type: T
                                                                          payload: {
                                                                            uniqueId: string
                                                                            address: string
                                                                            city?: string
                                                                            country?: string
                                                                            zipCode?: string
                                                                            state?: string
                                                                          }
                                                                          response: {
                                                                            consultation: Consultation
                                                                          }
                                                                        }
                                                                      : T extends ACTION_TYPE.CHECK_CONSULTATION_LIMIT_EXCEEDED
                                                                        ? {
                                                                            type: T
                                                                            payload: {
                                                                              uniqueId: string
                                                                              consultationType: AVAILABLE_SERVICE
                                                                            }
                                                                            response: boolean
                                                                          }
                                                                        : T extends ACTION_TYPE.SWAP_CONSULTATION
                                                                          ? {
                                                                              type: T
                                                                              payload: {
                                                                                uniqueId: string
                                                                                to: CONSULTATION_TYPE
                                                                              }
                                                                              response: Consultation
                                                                            }
                                                                          : T extends ACTION_TYPE.UPDATE_USER_LANGUAGE
                                                                            ? {
                                                                                type: T
                                                                                payload: {
                                                                                  userId: string
                                                                                  language: string
                                                                                }
                                                                                response: User
                                                                              }
                                                                            : T extends ACTION_TYPE.SET_FULFILLMENT_PATIENT_INFO
                                                                              ? {
                                                                                  type: T
                                                                                  payload: {
                                                                                    uniqueId: string
                                                                                    address: string
                                                                                    phone: string
                                                                                  }
                                                                                  response: {
                                                                                    consultation: Consultation
                                                                                  }
                                                                                }
                                                                              : T extends ACTION_TYPE.SEND_HEALTH_NAVIGATOR_MESSAGE
                                                                                ? {
                                                                                    type: T
                                                                                    payload: {
                                                                                      uniqueId: string
                                                                                      text: string
                                                                                    }
                                                                                    response: void
                                                                                  }
                                                                                : T extends ACTION_TYPE.START_HEALTH_NAVIGATOR
                                                                                  ? {
                                                                                      type: T
                                                                                      payload: {
                                                                                        uniqueId: string
                                                                                      }
                                                                                      response: HealthNavigatorResponse
                                                                                    }
                                                                                  : T extends ACTION_TYPE.FINISH_HEALTH_NAVIGATOR
                                                                                    ? {
                                                                                        type: T
                                                                                        payload: {
                                                                                          uniqueId: string
                                                                                        }
                                                                                        response: HealthNavigatorResponse
                                                                                      }
                                                                                    : T extends ACTION_TYPE.HEALTH_NAVIGATOR_SERVICE_SELECTED
                                                                                      ? {
                                                                                          type: T
                                                                                          payload: {
                                                                                            uniqueId: string
                                                                                            serviceType: AVAILABLE_SERVICE
                                                                                          }
                                                                                          response: HealthNavigatorResponse
                                                                                        }
                                                                                      : T extends ACTION_TYPE.REGISTER_REFERRAL
                                                                                        ? {
                                                                                            type: T
                                                                                            payload: {
                                                                                              uniqueId: string
                                                                                            }
                                                                                            response: {
                                                                                              consultation: Consultation
                                                                                            }
                                                                                          }
                                                                                        : T extends ACTION_TYPE.VIEW_REFERRAL_URGENCY
                                                                                          ? {
                                                                                              type: T
                                                                                              payload: {
                                                                                                uniqueId: string
                                                                                              }
                                                                                              response: {
                                                                                                consultation: Consultation
                                                                                              }
                                                                                            }
                                                                                          : T extends ACTION_TYPE.VIEW_REFERRAL_URGENCY
                                                                                            ? {
                                                                                                type: T
                                                                                                payload: {
                                                                                                  uniqueId: string
                                                                                                }
                                                                                                response: {
                                                                                                  consultation: Consultation
                                                                                                }
                                                                                              }
                                                                                            : T extends ACTION_TYPE.GET_ASSESSMENT_SURVEY_MODULE
                                                                                              ? {
                                                                                                  type: T
                                                                                                  payload: {
                                                                                                    consultationType: CONSULTATION_TYPE.MENTAL
                                                                                                    assessmentSurveyId: number
                                                                                                    uniqueId: string
                                                                                                  }
                                                                                                  response: Module
                                                                                                }
                                                                                              : T extends ACTION_TYPE.SET_ASSESSMENT_SURVEY_ANSWERS
                                                                                                ? {
                                                                                                    type: T
                                                                                                    payload: {
                                                                                                      uniqueId: string
                                                                                                      assessmentSurveyAnswer: AssessmentSurveyAnswer
                                                                                                    }
                                                                                                    response: AssessmentSurveyAnswer
                                                                                                  }
                                                                                                : T extends ACTION_TYPE.CREATE_MENTAL_HEALTH_CONSULTATION
                                                                                                  ? {
                                                                                                      type: T
                                                                                                      payload: {
                                                                                                        uniqueId: string
                                                                                                        question: string
                                                                                                      }
                                                                                                      response: Consultation
                                                                                                    }
                                                                                                  : T extends ACTION_TYPE.GET_CONVERSATION_SUMMARY
                                                                                                    ? {
                                                                                                        type: T
                                                                                                        payload: {
                                                                                                          uniqueId: string
                                                                                                          conversationId: string
                                                                                                          consultationType: AVAILABLE_SERVICE
                                                                                                        }
                                                                                                        response: {
                                                                                                          question: string
                                                                                                          attachments: string[]
                                                                                                        }
                                                                                                      }
                                                                                                    : T extends ACTION_TYPE.TRACK_EVENT
                                                                                                      ? {
                                                                                                          type: T
                                                                                                          payload: {
                                                                                                            userId: string
                                                                                                            event: PostMessage
                                                                                                          }
                                                                                                          response: void
                                                                                                        }
                                                                                                      : T extends ACTION_TYPE.IS_WEBSOCKET_CONNECTED
                                                                                                        ? {
                                                                                                            type: T
                                                                                                            payload: {
                                                                                                              uniqueId: string
                                                                                                              connectionId?: string
                                                                                                            }
                                                                                                            response: {
                                                                                                              isConnected: boolean
                                                                                                            }
                                                                                                          }
                                                                                                        : T extends ACTION_TYPE.STORE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK
                                                                                                          ? {
                                                                                                              type: T
                                                                                                              payload: {
                                                                                                                uniqueId: string
                                                                                                                feedbackId?: string | null
                                                                                                                messageId: string
                                                                                                              } & Pick<
                                                                                                                UserMessageFeedback,
                                                                                                                'ratingType' | 'rating' | 'comment'
                                                                                                              >
                                                                                                              response: HealthNavigatorResponse
                                                                                                            }
                                                                                                          : T extends ACTION_TYPE.DELETE_HEALTH_NAVIGATOR_MESSAGE_FEEDBACK
                                                                                                            ? {
                                                                                                                type: T
                                                                                                                payload: {
                                                                                                                  uniqueId: string
                                                                                                                  feedbackId: string
                                                                                                                  messageId: string
                                                                                                                }
                                                                                                                response: HealthNavigatorResponse
                                                                                                              }
                                                                                                            : T extends ACTION_TYPE.STORE_HEALTH_NAVIGATOR_CONVERSATION_FEEDBACK
                                                                                                              ? {
                                                                                                                  type: T
                                                                                                                  payload: {
                                                                                                                    uniqueId: string
                                                                                                                    conversationId: string
                                                                                                                  } & Pick<
                                                                                                                    UserMessageFeedback,
                                                                                                                    | 'ratingType'
                                                                                                                    | 'rating'
                                                                                                                    | 'comment'
                                                                                                                  >
                                                                                                                  response: HealthNavigatorResponse
                                                                                                                }
                                                                                                              : T extends ACTION_TYPE.CLOSE_CONSULTATION
                                                                                                                ? {
                                                                                                                    type: T
                                                                                                                    payload: {
                                                                                                                      uniqueId: string
                                                                                                                    }
                                                                                                                    response: Consultation
                                                                                                                  }
                                                                                                                : T extends ACTION_TYPE.CLOSE_SESSION
                                                                                                                  ? {
                                                                                                                      type: T
                                                                                                                      payload: {
                                                                                                                        uniqueId: string
                                                                                                                      }
                                                                                                                      response: void
                                                                                                                    }
                                                                                                                  : never
